import { films } from "@/mocks/films";
import { specials } from "@/mocks/specials";
import type { Film, Special } from "@/types/film";

export const allFilms: Film[] = films as Film[];
export const allSpecials: Special[] = specials as Special[];

export const CATEGORY_KEYS = ["电影", "电视剧", "短片"];

export const categoryMeta: Record<
  string,
  { name: string; en: string; icon: string; description: string; longDescription: string }
> = {
  电影: {
    name: "电影",
    en: "Feature Films",
    icon: "ri-movie-2-line",
    description: "长片篇幅里的人物关系与道德困境",
    longDescription:
      "电影以完整的篇幅展开人物关系与道德困境，是日韩伦理题材最成熟的表达形式。本分类收录院线与影展长片，侧重叙事结构、表演与镜头语言的分析。",
  },
  电视剧: {
    name: "电视剧",
    en: "Series",
    icon: "ri-tv-2-line",
    description: "多集长度容纳群像与日常",
    longDescription:
      "电视剧用多集长度容纳群像与日常，秘密往往藏在走廊、餐桌与夜班之间。本分类收录限定剧与迷你剧，关注人物关系的长期变化与社会议题的铺陈。",
  },
  短片: {
    name: "短片",
    en: "Short Films",
    icon: "ri-clapperboard-line",
    description: "三十分钟内完成一次伦理提问",
    longDescription:
      "短片没有时间铺陈，只能把问题直接摆到桌面上。本分类收录三十分钟以内的作品，它们通常只有一个场景、一段对话，却能留下很长的回响。",
  },
};

export const GENRE_OPTIONS = ["伦理剧情", "爱情伦理", "家庭伦理", "悬疑伦理", "犯罪伦理"];

export const regionOptions = [
  { key: "all", label: "全部地区" },
  { key: "日本", label: "偏日本" },
  { key: "韩国", label: "偏韩国" },
];

export const decadeOptions = [
  { key: "all", label: "全部年代" },
  { key: "2020", label: "2020 年代" },
  { key: "2010", label: "2010 年代" },
];

export const sortOptions = [
  { key: "popular", label: "按热度排序" },
  { key: "latest", label: "按最新上线" },
  { key: "score", label: "按评分排序" },
  { key: "year", label: "按年份排序" },
];

export function getFilmById(id?: string): Film | undefined {
  if (!id) return undefined;
  return allFilms.find((film) => film.id === id);
}

export function getSpecialById(id?: string): Special | undefined {
  if (!id) return undefined;
  return allSpecials.find((item) => item.id === id);
}

export function getSpecialFilms(special: Special): Film[] {
  return special.filmIds
    .map((filmId) => allFilms.find((film) => film.id === filmId))
    .filter((film): film is Film => Boolean(film));
}

export function countByCategory(category: string): number {
  return allFilms.filter((film) => film.category === category).length;
}

export function getFilmsByCategory(category: string): Film[] {
  return allFilms.filter((film) => film.category === category);
}

export function getFeaturedFilms(): Film[] {
  return allFilms.filter((film) => film.featured);
}

export function getLatestFilms(limit = 8): Film[] {
  return [...allFilms]
    .sort((a, b) => b.addedAt.localeCompare(a.addedAt))
    .slice(0, limit);
}

export function getEditorPicks(limit = 5): Film[] {
  return allFilms
    .filter((film) => film.editorPick)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);
}

export function getPopularFilms(limit = 4): Film[] {
  return [...allFilms].sort((a, b) => b.popularity - a.popularity).slice(0, limit);
}

interface FilterOptions {
  category?: string;
  region?: string;
  decade?: string;
  genre?: string;
  sort?: string;
}

export function filterFilms({
  category,
  region = "all",
  decade = "all",
  genre = "all",
  sort = "popular",
}: FilterOptions): Film[] {
  const result = allFilms.filter((film) => {
    if (category && film.category !== category) return false;
    if (region !== "all" && film.region !== region) return false;
    if (decade !== "all" && Math.floor(film.year / 10) * 10 !== Number(decade)) return false;
    if (genre !== "all" && !film.genres.includes(genre)) return false;
    return true;
  });

  const sorted = [...result];
  if (sort === "latest") {
    sorted.sort((a, b) => b.addedAt.localeCompare(a.addedAt));
  } else if (sort === "score") {
    sorted.sort((a, b) => b.score - a.score);
  } else if (sort === "year") {
    sorted.sort((a, b) => b.year - a.year);
  } else {
    sorted.sort((a, b) => b.popularity - a.popularity);
  }
  return sorted;
}

export function searchFilms(
  keyword: string,
  options: { category?: string; decade?: string } = {},
): Film[] {
  const query = keyword.trim().toLowerCase();
  if (!query) return [];

  return allFilms.filter((film) => {
    if (options.category && options.category !== "all" && film.category !== options.category) {
      return false;
    }
    if (
      options.decade &&
      options.decade !== "all" &&
      Math.floor(film.year / 10) * 10 !== Number(options.decade)
    ) {
      return false;
    }
    const haystack = [
      film.title,
      film.titleEn,
      film.director,
      film.cast.join(" "),
      film.genres.join(" "),
      film.tags.join(" "),
      film.summary,
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(query);
  });
}

export function getRelatedFilms(film: Film, limit = 4): Film[] {
  return allFilms
    .filter((item) => item.id !== film.id)
    .map((item) => {
      let weight = 0;
      if (item.category === film.category) weight += 2;
      if (item.region === film.region) weight += 1;
      weight += item.genres.filter((genre) => film.genres.includes(genre)).length * 3;
      weight += item.tags.filter((tag) => film.tags.includes(tag)).length * 2;
      return { item, weight };
    })
    .sort((a, b) => b.weight - a.weight || b.item.popularity - a.item.popularity)
    .slice(0, limit)
    .map((entry) => entry.item);
}

export function getOtherSpecials(currentId: string, limit = 2): Special[] {
  return allSpecials.filter((item) => item.id !== currentId).slice(0, limit);
}

export function paginate<T>(items: T[], page: number, pageSize: number): T[] {
  const start = (page - 1) * pageSize;
  return items.slice(start, start + pageSize);
}

export function totalPages(count: number, pageSize: number): number {
  return Math.max(1, Math.ceil(count / pageSize));
}

export function formatDate(value: string): string {
  return value.replace(/-/g, ".");
}